import { useCallback, useRef } from 'react'

import config from '@config'
import useTracking, { TrackingFnType } from '@hooks/useTracking'

import {
  doParseStringifiedData,
  enableSubscriptionAutoRenewal,
  getTrackingCheckoutParams,
  getTrackingTemplateParams,
  setExperienceCookie,
  setTrackingCheckoutParams,
  setIsArticleFreeToReadCookie,
  getElementTrackingParam,
} from './utils'
import useAuthentication from '@hooks/useAuthentication'
import {
  LoginFromPiano,
  NonSiteActionFn,
  OnExperienceExecuteParams,
  OnShowOfferParams,
  OnCheckoutCompleteFn,
  PianoEvents,
  PianoTrackingEvent,
  SubmitPaymentParams,
  OnCheckoutCloseParams,
  PianoCheckoutParams,
  OnCompleteUpgradePurchaseParams,
  OnSetResponseVariableParams,
  OneLogLoginCases,
} from './types'
import { useQueryClient } from '@tanstack/react-query'
import { User } from '@sentry/types'
import { useRouter } from 'next/router'
import { captureExceptionWithUser } from '@utils/sentry'
import useExecuteOnClientNavigation from '@hooks/useExecuteOnClientNavigation'

const {
  auth: { ssoSourceSuffix },
  piano: { blickPlusOfferCardName, regwallTemplateCardName, applicationId },
  subscriptions: {
    conversionUrl,
    footerBannerClassname,
    restrictedDrawerClassname,
  },
} = config

const usePianoEvents = (): PianoEvents => {
  const queryClient = useQueryClient()
  const router = useRouter()
  const oneLogLoginCases = useRef<OneLogLoginCases>({})

  const { loginWithSource } = useAuthentication()

  const trackPianoEvent = useCallback<TrackingFnType<PianoTrackingEvent>>(
    ({ extraData }) => {
      const { type, params } = extraData

      const pianoTrackingTemplateNameParams = getTrackingTemplateParams(
        params?.experienceActionId
      )

      return type
        ? {
            event: type,
            piano_offer_id: params?.offerId,
            piano_template_id: params?.templateId ?? 'MAIN',
            piano_template_variant_id: params?.templateVariantId ?? 'MAIN',
            piano_experience_id: params?.experienceId,
            piano_term_id: params?.termid,
            piano_term_name: params?.termname,
            piano_term_conversion_id: params?.termConversionId,
            price: params?.price,
            currency: params?.currency,
            transaction_id: params?.transactionId,
            coupon: params?.coupon,
            button_text: params?.buttontext,
            element: params?.clickelement,
            target_url: params?.targeturl,
            element_status: params?.state,
            ...pianoTrackingTemplateNameParams,
          }
        : undefined
    },
    []
  )

  const trackingFunction = useTracking<PianoTrackingEvent>(trackPianoEvent)

  const doTrackPianoEvent = useCallback(
    (trackingEvent: PianoTrackingEvent) => {
      if (trackingEvent?.type && !trackingEvent.exception) {
        trackingFunction(trackingEvent)
      }
    },
    [trackingFunction]
  )

  const trackUnlockRestrictedContent = useCallback<
    TrackingFnType<{ paywall_bypass: string }>
  >(
    ({ extraData }) => ({
      event: 'unlock_content',
      ...extraData,
    }),
    []
  )

  const handleTrackUnlockRestrictedContent = useTracking(
    trackUnlockRestrictedContent
  )

  const loginFromPiano: LoginFromPiano = (
    params,
    loginCase,
    isCheckout = false
  ) => {
    // Only set a cookie, if it's a click that happened on a startCheckout button in the Piano template
    // Otherwise an offer could be shown for Logins, where we don't want to show the user an offer
    if (isCheckout) {
      setExperienceCookie(params)
    }

    const { offerId, templateId, templateVariantId, experienceId } = params

    // Gets the source from pianos custom variable, set on every page change
    // and appends the piano specific event data
    const source = `${
      window?.tp?.customVariables?.sourceSSO
    }_${offerId}_${templateId}${
      templateVariantId ? `_${templateVariantId}` : '_MAIN' // Tracking team requested to hardcode a value in case variant is undefined
    }_${experienceId}_${ssoSourceSuffix}`

    loginWithSource({
      source,
      loginCase: loginCase || 'email_only',
    })
  }

  const onLoginRequired = (params: any) => {
    // A OneLog login case can be different depending on the term or the experience
    // This login case can be defined in the Piano response variables
    // It will either take login case defined per term (login_case_termId), per experience (login_case) or fall back to
    // the default Piano login case (blickplus_piano)
    const loginCase =
      oneLogLoginCases.current[`login_case_${params.termId}`] ??
      oneLogLoginCases.current.login_case ??
      'blickplus_piano'

    loginFromPiano(params, loginCase, true)
  }

  const onCheckoutCustom = (event: any) => {
    const { eventName } = event
    const { params, buttontext, ...rest } = event?.params
    const parsedParams = doParseStringifiedData(params)
    const userData = queryClient.getQueryData<User>(['user'])

    const mergedParams = {
      ...(parsedParams ?? {}), // data about Piano experience metadata
      ...rest, // external events in Piano template
    }

    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient
    ) as PianoCheckoutParams

    setTrackingCheckoutParams(queryClient, {
      ...previousPianoCheckoutParams,
      ...mergedParams,
    })

    switch (eventName) {
      case 'piano_click':
      case 'piano_click_offer':
        if (rest.clickelement === 'login' && !userData) {
          loginFromPiano(parsedParams, rest.logincase, false)
        }

        doTrackPianoEvent({
          type: eventName,
          params: { ...mergedParams, buttontext },
        })

        break

      case 'adblock_interaction':
        doTrackPianoEvent({
          type: eventName,
          params: { ...mergedParams, eventCategory: 'adblock' },
        })

        break

      case 'piano_click_close':
        doTrackPianoEvent({
          type: eventName,
          params: mergedParams,
        })

        break

      case 'enable_autorenewal':
        enableSubscriptionAutoRenewal(queryClient, mergedParams)

        doTrackPianoEvent({
          type: 'piano_click',
          params: mergedParams,
        })

        break

      case 'show_free_article':
        setIsArticleFreeToReadCookie(router, mergedParams?.url)

        doTrackPianoEvent({
          type: 'piano_click',
          params: { ...mergedParams, buttontext },
        })

        break
    }
  }

  const setIsContentRestricted = useCallback(() => {
    queryClient.setQueryData(['isContentRestricted'], true)
    queryClient.invalidateQueries({
      queryKey: ['isContentRestricted'],
      exact: true,
    })
  }, [queryClient])

  const onShowOffer = (params: OnShowOfferParams) => {
    handleTracking(params)
  }

  const handleTracking = (params: OnShowOfferParams) => {
    const { displayMode } = params
    if (
      displayMode === 'modal' ||
      params.containerSelector === `.${footerBannerClassname}` ||
      params.containerSelector === `.${restrictedDrawerClassname}`
    ) {
      doTrackPianoEvent({ type: 'piano_show', params })
    } else {
      // Impression tracking for templates that should not immediately be tracked
      const pianoTemplateParams = queryClient.getQueryData<OnShowOfferParams[]>(
        ['pianoTemplateParams']
      )

      // As there can be multiple templates on one page and the impression tracking will be called
      // when the template comes into the viewport, we need to save the template params in a template params array
      const pianoTemplateParamsArray = pianoTemplateParams || []
      pianoTemplateParamsArray.push(params)

      queryClient.setQueryData(
        ['pianoTemplateParams'],
        pianoTemplateParamsArray
      )
      queryClient.invalidateQueries({
        queryKey: ['pianoTemplateParams'],
        exact: true,
      })
    }
  }

  const onShowTemplate = (params: OnShowOfferParams) => {
    handleTracking(params)
  }

  const onExperienceExecute = (params: OnExperienceExecuteParams) => {
    const experienceEvents = params.result.events

    const isPaywallTemplateShown = experienceEvents.some(
      (experienceEvent) =>
        experienceEvent.eventType === 'showOffer' &&
        experienceEvent.eventModuleParams?.moduleName === blickPlusOfferCardName
    )
    const isRegwallTemplateShown = experienceEvents.some(
      (experienceEvent) =>
        experienceEvent.eventType === 'showTemplate' &&
        experienceEvent.eventModuleParams?.moduleName ===
          regwallTemplateCardName
    )

    const isContentRestricted = isPaywallTemplateShown || isRegwallTemplateShown

    if (isContentRestricted) {
      setIsContentRestricted()
    }
  }

  const onCustomEventTracking: NonSiteActionFn = (
    _eventParams,
    cardParams,
    contextParams
  ) => {
    //@ts-expect-error
    const cardNameChunks = cardParams.moduleName.split(/_(.*)/s)
    if (!cardNameChunks?.length) {
      console.error(
        `[Piano tracking] Invalid event name for nonSite action: ${cardParams.moduleName}`
      )
      return
    }
    // Event name is whatever is after the first "_" in the card name
    const [eventPrefix, eventName] = cardNameChunks
    if (eventPrefix === 'track' && eventName) {
      const { experienceId } = contextParams
      doTrackPianoEvent({ type: eventName, params: { experienceId } })
    }
  }

  const onSubmitPayment = (params: SubmitPaymentParams) => {
    const {
      term: { chargeCurrency, chargeAmount, billingPlanTable },
    } = params

    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient
    ) as PianoCheckoutParams

    const trackingRelevantParams = {
      ...previousPianoCheckoutParams,
      variant: billingPlanTable[0]?.period,
      price: chargeAmount,
      currency: chargeCurrency,
    }

    // Update the checkout params with variant, price and currency
    setTrackingCheckoutParams(queryClient, trackingRelevantParams)

    doTrackPianoEvent({
      type: 'purchase_attempt',
      params: trackingRelevantParams,
      exception: previousPianoCheckoutParams.clickelement === 'registration',
    })
  }

  const onCompleteUpgradePurchase = ({
    termConversionId,
  }: OnCompleteUpgradePurchaseParams) => {
    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient
    ) as PianoCheckoutParams

    doTrackPianoEvent({
      type: 'purchase_success',
      params: {
        ...previousPianoCheckoutParams,
        termConversionId,
        price: previousPianoCheckoutParams.adjustedprice,
      },
    })
  }

  const onCheckoutComplete: OnCheckoutCompleteFn = async (conversionObj) => {
    const { paymentId, termConversionId } = conversionObj

    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient
    ) as PianoCheckoutParams

    // On registration conversions we need to ignore the currency, price and the transactionId
    if (conversionObj?.type === 'registration') {
      if (previousPianoCheckoutParams) {
        const rest = { ...previousPianoCheckoutParams }
        delete rest.currency
        delete rest.price

        doTrackPianoEvent({
          type: 'registration_success',
          params: {
            ...rest,
            termConversionId,
          },
        })
      }
    }
    // On default checkout conversions we need to supply tracking with the below parameters
    else {
      doTrackPianoEvent({
        type: 'purchase_success',
        params: {
          ...previousPianoCheckoutParams,
          transactionId: paymentId,
          termConversionId,
        },
      })
    }

    const payload = { aid: applicationId, ...conversionObj }
    const response = await fetch(conversionUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      captureExceptionWithUser(
        `[SUB. CHECKER ERROR] POST conversion returned an error: ${response.statusText}`,
        queryClient
      )
    }
  }

  const onCheckoutClose = (event: OnCheckoutCloseParams) => {
    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient
    ) as PianoCheckoutParams

    // Piano sends the checkout close event also on template events (e.g. footer banner)
    // We only want to track the checkout_close if it's really from the checkout template
    // The termId is only available after a user started the checkout
    if (previousPianoCheckoutParams.termId) {
      doTrackPianoEvent({
        type: 'checkout_close',
        params: {
          ...previousPianoCheckoutParams,
          state: event.state,
        },
        exception: previousPianoCheckoutParams.clickelement === 'registration',
      })

      // Reset checkout parameters when checkout is closed
      setTrackingCheckoutParams(queryClient, null)
    }

    // Default behavior is to refresh the page on successful checkout
    if (event.state === 'checkoutCompleted') {
      location?.reload?.()
    }
  }

  const onCheckoutSelectTerm = () => {
    const previousPianoCheckoutParams = getTrackingCheckoutParams(
      queryClient
    ) as PianoCheckoutParams

    const trackingRelevantParams = {
      ...previousPianoCheckoutParams,
      clickelement: getElementTrackingParam(
        previousPianoCheckoutParams.clickelement
      ),
    }

    // Add correct clickelement to the tracking checkout params
    setTrackingCheckoutParams(queryClient, trackingRelevantParams)

    doTrackPianoEvent({
      type: 'start_checkout',
      params: trackingRelevantParams,
      exception: previousPianoCheckoutParams.clickelement === 'registration',
    })
  }

  const onSetResponseVariable = ({
    responseVariables,
  }: OnSetResponseVariableParams) => {
    oneLogLoginCases.current = responseVariables

    const paywall_bypass = responseVariables['paywall_bypass']
    if (paywall_bypass) {
      handleTrackUnlockRestrictedContent({ paywall_bypass })
    }
  }

  // As we have an SPA and Piano doesn't clean up the response variables,
  // we have to do this by ourselves, whenever a page change happens
  const resetResponseVariables = useCallback(() => {
    oneLogLoginCases.current = {}
  }, [])

  useExecuteOnClientNavigation(resetResponseVariables)

  return {
    onCheckoutClose,
    onCheckoutCustom,
    onShowOffer,
    onShowTemplate,
    onCustomEventTracking,
    onLoginRequired,
    onSubmitPayment,
    onExperienceExecute,
    onCheckoutComplete,
    onCheckoutSelectTerm,
    onCompleteUpgradePurchase,
    onSetResponseVariable,
  }
}

export default usePianoEvents
